import { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { parse } from 'date-fns/parse';
import { useTranslation } from 'react-i18next';
import { object, number, string } from 'yup';
import { Input, message, Table, Typography } from 'antd';
import { PaymentHttp } from '@/services';
import { useAppStore } from '@/store';
import { AppButton, Flex, InputNumeric } from '@/components';
import { format as formatDate } from 'date-fns-jalali/format';
import { useWalletPageContext } from './WalletPageContext';

export interface TransferCreditProps {
  isSubsidy: boolean;
  transferInfo: PaymentHttp.TransferInfo;
}

const INPUT_STYLE = {
  fontSize: '0.875rem',
  border: '2px solid #d9d8d8',
};

export function TransferCredit({ isSubsidy, transferInfo }: TransferCreditProps) {

  const [t] = useTranslation(['common', 'payment']);
  const { hasSubsidy } = useWalletPageContext();
  const [submitted, setSubmitted] = useState(false);
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const [fetchCredit, subsidies] = useAppStore(state => [state.fetchCredit, state.subsidies]);

  const selectedSubsidy = useMemo(() => {
    return subsidies.find((_, index) => index === selectedRowKey);
  }, [subsidies, selectedRowKey]);

  const limits = useMemo(() => {
    if (isSubsidy) {
      if (!selectedSubsidy) {
        return { min: 0, max: 0 };
      }
      return {
        min: 1,
        max: selectedSubsidy?.credit
      };
    }
    if (!transferInfo) {
      return { min: 0, max: 0 };
    }
    return {
      min: transferInfo?.minCreditTransferAmount / 10,
      max: transferInfo?.maxCreditTransferAmount / 10
    };
  }, [isSubsidy, selectedSubsidy, transferInfo]);

  const formik = useFormik({
    validationSchema: object({
      nurtureProfileId: number().integer().nullable().default(null),
      username: string().required().min(3).label(t('common:labels.username')),
      amount: number()
        .required()
        .integer()
        .positive()
        .min(limits.min)
        .max(limits.max)
        .label(t('payment:labels.amount'))
    }),
    initialValues: {
      username: '',
      amount: undefined as number,
      nurtureProfileId: null
    },
    onSubmit: async ({ username, amount, nurtureProfileId }) => {
      try {
        await PaymentHttp.transferCredit({ username, amount, nurtureProfileId });
        setSubmitted(false);
        formik.resetForm();
        fetchCredit();
        message.success(t('payment:messages.success-transfer-credit'));
      }
      catch (e) {
      }
    },
  });

  return (
    <Flex direction="column" className="flex-grow">
      {isSubsidy && (
        hasSubsidy && subsidies.length > 0 &&
        <Flex direction="column" className="w-full rounded text-sm">
          <Table
            size="small"
            className="!text-sm rounded"
            pagination={false}
            rowSelection={{
              type: 'radio',
              onChange: ([selectedRowKey]) => {
                setSelectedRowKey(selectedRowKey);
                const subsidy = subsidies.find((_, index) => index === selectedRowKey);
                if (!subsidy) return;
                formik.setFieldValue('amount', (subsidy?.credit || 0) / 10);
                formik.setFieldValue('nurtureProfileId', subsidy?.id || 0);
              }
            }}
            columns={[
              { dataIndex: 'index', title: t('common:labels.id'), width: '50px' },
              { dataIndex: 'credit', title: t('common:labels.credit') },
              { dataIndex: 'expiration', title: t('common:labels.expiration') },
            ]}
            dataSource={(subsidies ?? []).map((item, index) => {
              const date = parse(item?.expirationDate, 'yyyy-MM-dd HH:mm:ss', new Date());
              const formattedDate = formatDate(date, 'yyyy/MM/dd');
              return {
                key: index,
                index: index + 1,
                credit: (item?.credit || 0) / 10,
                expiration: formattedDate
              }
            })}
          />
        </Flex>
      )}
      {!isSubsidy && (
        <div className="py-3 px-2 leading-normal text-start text-nurture-darker text-sm mb-0">
          <ul className="!list-disc my-0" style={{ marginInlineStart: '-2rem' }}>
            {transferInfo?.messages.map((msg, i) => <li key={i}>{msg}</li>)}
          </ul>
        </div>
      )}
      <Flex direction="column" gap={10} className="mt-auto">
        <Input
          size="large"
          name="username"
          style={INPUT_STYLE}
          onChange={formik.handleChange}
          value={formik.values.username}
          disabled={isSubsidy && selectedRowKey == null}
          placeholder={t('payment:labels.username-credit-recipient')}
        />
        <InputNumeric
          size="large"
          style={INPUT_STYLE}
          value={formik.values.amount}
          min={limits.min}
          max={limits.max}
          disabled={isSubsidy && selectedRowKey == null}
          onChange={value => formik.setFieldValue('amount', value)}
          placeholder={t('payment:labels.amount-toman-min', { min: limits.min })}
        />
        {submitted && Object.keys(formik.errors).length > 0 &&
          <Typography.Text className="text-xs" type="danger">
            {formik.errors[Object.keys(formik.errors)[0]]}
          </Typography.Text>
        }

        <AppButton
          size="middle"
          appVariant="submit"
          disabled={isSubsidy && selectedRowKey == null}
          onClick={() => {
            setSubmitted(true);
            formik.submitForm();
          }}
        >
          {t('payment:labels.transfer-credit')}
        </AppButton>
      </Flex>
    </Flex>
  );
};

export default TransferCredit;
